// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

// Deployment 17 — P3 — Confronto Conti Deposito
// Pagina educativa, dati indicativi aprile 2026.

const BANCHE = [
  {
    id: 'banca-progetto',
    nome: 'Banca Progetto',
    sede: 'Italia',
    garanzia: 'FITD (Fondo Interbancario Tutela Depositi) €100.000',
    tipo: 'Libero e vincolato (12, 24, 36, 48, 60 mesi)',
    minimo: '€10.000',
    massimo: 'Senza limite massimo',
    url: 'https://www.bancaprogetto.it',
  },
  {
    id: 'banca-ifis',
    nome: 'Banca Ifis',
    sede: 'Italia',
    garanzia: 'FITD €100.000',
    tipo: 'Libero e vincolato (3, 6, 12, 24, 36, 48, 60 mesi)',
    minimo: 'Nessun minimo (con vincolato dal primo deposito)',
    massimo: 'Fino a €1.500.000',
    url: 'https://www.rendimax.it',
  },
  {
    id: 'mobilize',
    nome: 'Mobilize Financial Services (ex Renault Bank)',
    sede: 'Francia',
    garanzia: 'Fonds de Garantie des Dépôts (FGDR), equivalente FITD €100.000',
    tipo: 'Libero e vincolato (12, 24 mesi)',
    minimo: '€100',
    massimo: 'Fino a €1.000.000',
    url: 'https://www.mobilize-fs.it',
  },
  {
    id: 'santander',
    nome: 'Santander',
    sede: 'Spagna',
    garanzia: 'Fondo de Garantía de Depósitos (FGD), equivalente FITD €100.000',
    tipo: 'Vincolato (3, 6, 12, 24, 36 mesi)',
    minimo: '€5.000',
    massimo: 'Fino a €5.000.000',
    url: 'https://www.openbank.it',
  },
  {
    id: 'banca-mediolanum',
    nome: 'Banca Mediolanum',
    sede: 'Italia',
    garanzia: 'FITD €100.000',
    tipo: 'Libero e vincolato (offerta variabile)',
    minimo: 'Variabile in base al prodotto',
    massimo: 'Variabile',
    url: 'https://www.bancamediolanum.it',
  },
];

const ConfrontoContiDeposito = () => (
  <div className="min-h-screen bg-[#1C1C1A]" data-testid="confronto-conti-deposito-page">
    <Helmet>
      <title>Confronto Conti Deposito 2026 — Tassi e condizioni | SAPIENS</title>
      <meta name="description" content="Confronto dei principali conti deposito per risparmiatori italiani 2026: Banca Progetto, Banca Ifis, Mobilize, Santander, Banca Mediolanum." />
      <link rel="canonical" href="https://finsapiens.it/risorse/confronto-conti-deposito" />
      <meta property="og:title" content="Confronto Conti Deposito 2026 — Tassi e condizioni | SAPIENS" />
      <meta property="og:description" content="Confronto dei principali conti deposito per risparmiatori italiani." />
    </Helmet>
    <div className="container-wide py-10 max-w-5xl">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-medium text-[#E8E8E4] mb-3">Confronto Conti Deposito 2026</h1>
        <p className="text-[#888780]">Confronto aggiornato dei 5 principali conti deposito per risparmiatori italiani.</p>
      </motion.div>

      <div className="sapiens-note-box mb-6 text-sm" data-testid="confronto-conti-fitd-note">
        Tutti i conti deposito elencati sono garantiti fino a €100.000 per depositante per banca dal sistema di garanzia del paese d'origine — equivalente al FITD italiano per le banche straniere.
      </div>

      <div className="space-y-4">
        {BANCHE.map((b) => (
          <div key={b.id} data-testid={`banca-${b.id}`} className="rounded-xl p-5 bg-[#252523] border border-white/[0.06]">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
              <div>
                <h3 className="text-lg font-semibold text-[#E8E8E4]">{b.nome}</h3>
                <p className="text-xs text-[#888780] mt-1">{b.sede} · {b.garanzia}</p>
              </div>
              <a href={b.url} target="_blank" rel="noreferrer" className="text-sm text-[#C9A84C] hover:underline flex items-center gap-1.5">
                Tassi aggiornati sul sito ufficiale <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 text-sm text-[#C5C5BE]">
              <div><p className="text-xs text-[#888780] uppercase mb-1">Tipo</p>{b.tipo}</div>
              <div><p className="text-xs text-[#888780] uppercase mb-1">Importo minimo</p>{b.minimo}</div>
              <div><p className="text-xs text-[#888780] uppercase mb-1">Importo massimo</p>{b.massimo}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="sapiens-note-box mt-6 text-sm" data-testid="confronto-conti-tassi-note">
        I tassi cambiano frequentemente — visita il sito ufficiale della banca per i tassi aggiornati. Per un confronto in tempo reale usa <a href="https://www.confrontaconti.it" target="_blank" rel="noreferrer" className="text-[#C9A84C] hover:underline">confrontaconti.it</a>.
      </div>

      <div className="sapiens-note-box mt-3 text-xs" data-testid="confronto-conti-disclaimer">
        Le informazioni presenti in questa pagina hanno scopo esclusivamente educativo e non costituiscono raccomandazione finanziaria. SAPIENS non ha accordi commerciali con nessuna delle banche elencate.
      </div>
    </div>
  </div>
);

export default ConfrontoContiDeposito;
